import React, { useEffect, useState } from "react";
import VoicePanelShell from "./VoicePanelShell";

export type VoicePanelPhase =
  | "permissionPrompt"
  | "modelLoading"
  | "listening"
  | "transcribing"
  | "interpreting";

interface VoiceListeningPanelProps {
  phase: VoicePanelPhase;
  /** Seconds elapsed since recording started — only meaningful during "listening". */
  elapsedSeconds: number;
  /** Live mic RMS level (0..1), only meaningful during "listening" — drives the waveform height. */
  level?: number;
  /** 0..1, only meaningful during "modelLoading". */
  modelLoadProgress?: number;
  /** Example phrase shown under the waveform while listening. */
  examplePrompt?: string;
  /** Stops recording and hands off what was heard. Only offered during "listening". */
  onStop: () => void;
  /** Abandons the attempt and puts the search form back. */
  onCancel: () => void;
}

const BAR_COUNT = 56;

// A fixed envelope (loud in the middle, tapering to the edges) times the live
// mic level: the shape stays recognisable while the height tracks the voice.
const BAR_ENVELOPE = Array.from({ length: BAR_COUNT }, (_, i) => {
  const t = i / (BAR_COUNT - 1);
  const taper = Math.sin(Math.PI * t);
  const ripple = 0.55 + 0.45 * Math.abs(Math.sin(t * Math.PI * 3.1));
  return Math.max(0.12, taper * ripple);
});

const MIN_BAR_HEIGHT = 4;
const MAX_BAR_HEIGHT = 56;
// Purely visual — not the VAD decision that gates whether whisper gets called
// (see core/vad.ts for that).
const SPEAKING_LEVEL_THRESHOLD = 0.02;

function formatElapsed(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

/**
 * Real seconds spent in the current stage. Deliberately a measured count, not
 * a synthetic percentage: whisper's progress_callback can't report anything
 * between 0% and 100% for a single sub-30s window (see the vendor README), so
 * a percentage bar here would be inventing numbers. A ticking clock is the
 * honest signal that work is still happening.
 *
 * Lives in this component, so it starts when the panel mounts and is cleared
 * when it unmounts — it can never outlive the voice interaction.
 */
function useStageSeconds(active: boolean): number {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (!active) {
      setSeconds(0);
      return;
    }
    const startedAt = Date.now();
    setSeconds(0);
    const id = setInterval(() => {
      setSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 250);
    return () => clearInterval(id);
  }, [active]);

  return seconds;
}

// The three stages the user actually waits through, in order. The two setup
// phases (permission, model load) aren't part of the run and don't appear.
const STAGES: { phase: VoicePanelPhase; label: string }[] = [
  { phase: "listening", label: "Listening" },
  { phase: "transcribing", label: "Understanding" },
  { phase: "interpreting", label: "Finding flights" },
];

const PHASE_COPY: Record<VoicePanelPhase, { title: string; sub?: string }> = {
  permissionPrompt: { title: "Waiting for microphone permission..." },
  modelLoading: { title: "Preparing voice search..." },
  listening: { title: "Listening ..." },
  // Each wait gets its own name: the encode is a fixed multi-second cost and
  // the round-trip is about a second, and they feel very different. Telling
  // the user which one they're in is most of the perceived-speed win.
  transcribing: { title: "Understanding what you said", sub: "Sending your recording for transcription" },
  interpreting: { title: "Finding your flights", sub: "Matching airports, dates and routes" },
};

/**
 * The panel that replaces the search card from the moment the mic is tapped
 * until the transcript is ready. The waveform reacts to the real mic level
 * while recording; once recording stops it hands over to the stage indicator,
 * which names the phase being waited on. Nothing is transcribed on screen
 * while recording — whisper only runs once the clip is finished.
 *
 * Rendered only while voice search is mid-run (see isVoicePanelActive in
 * SearchByRoute): idle, confirm, review and every terminal state unmount it,
 * so nothing here can linger after the run ends or survive a cancel.
 */
const VoiceListeningPanel: React.FC<VoiceListeningPanelProps> = ({
  phase,
  elapsedSeconds,
  modelLoadProgress = 0,
  level = 0,
  examplePrompt = "Flights From dubai to Cochin, Tomorrow.",
  onStop,
  onCancel,
}) => {
  const isListening = phase === "listening";
  const isProcessing = phase === "transcribing" || phase === "interpreting";
  const isSpeaking = isListening && level > SPEAKING_LEVEL_THRESHOLD;
  const copy = PHASE_COPY[phase];

  const activeStageIndex = STAGES.findIndex((stage) => stage.phase === phase);
  const stageSeconds = useStageSeconds(isProcessing);

  // Log-ish scaling: speech RMS mostly lives in the low tenths, so a linear
  // map would leave the waveform nearly flat the whole time.
  const amplitude = isSpeaking ? Math.min(1, Math.sqrt(level) * 2.4) : 0;

  return (
    <VoicePanelShell onClose={onCancel}>
      <div className="flex flex-col items-center justify-center">
        <button
          type="button"
          onClick={isListening ? onStop : undefined}
          disabled={!isListening}
          aria-label={isListening ? "Stop recording" : copy.title}
          className="flex h-[64px] w-full max-w-[900px] items-center justify-center gap-[3px] disabled:cursor-default cursor-pointer"
        >
          {BAR_ENVELOPE.map((envelope, i) => {
            const height = isListening
              ? MIN_BAR_HEIGHT + envelope * amplitude * (MAX_BAR_HEIGHT - MIN_BAR_HEIGHT)
              : MIN_BAR_HEIGHT + envelope * 0.35 * (MAX_BAR_HEIGHT - MIN_BAR_HEIGHT);
            return (
              <span
                key={i}
                className={`w-[3px] shrink-0 rounded-full bg-gradient-to-b from-[#FFD980] via-[#FFBC00] to-[#8A5E00] transition-[height,opacity] duration-150 ease-out ${
                  isProcessing ? "voice-eq-bar-idle" : ""
                }`}
                style={
                  {
                    height: `${Math.round(height)}px`,
                    opacity: isListening ? (isSpeaking ? 1 : 0.4) : 0.45,
                    animationDelay: `${(i % 8) * 0.07}s`,
                  } as React.CSSProperties
                }
              />
            );
          })}
        </button>

        {phase === "modelLoading" ? (
          <>
            <p className="mt-5 font-inter text-sm text-white/80">{copy.title}</p>
            <div className="mt-3 h-1.5 w-full max-w-[280px] overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full bg-[#FFBC00] transition-all duration-200"
                style={{ width: `${Math.round(modelLoadProgress * 100)}%` }}
              />
            </div>
          </>
        ) : isListening ? (
          <>
            <p className="mt-4 max-w-[900px] text-center font-inter text-base text-white/85 sm:text-lg">
              Try saying: &quot;{examplePrompt}&quot;
            </p>
            <p className="mt-1 font-inter text-xs text-white/40">
              {formatElapsed(elapsedSeconds)} · tap the waveform to stop
            </p>
          </>
        ) : (
          <>
            <p className="mt-5 font-inter text-base text-white">{copy.title}</p>
            {copy.sub && <p className="mt-1 font-inter text-sm text-white/60">{copy.sub}</p>}
            {isProcessing && (
              <p className="mt-1 font-inter text-xs tabular-nums text-white/40">
                {formatElapsed(stageSeconds)}
              </p>
            )}
          </>
        )}

        {/* Stage indicator: which of the three waits the user is in. Hidden
            during permission/model-load, which aren't part of a run. */}
        {activeStageIndex >= 0 && (
          <div
            className="mt-5 flex items-center gap-2"
            role="status"
            aria-live="polite"
            aria-label={`Step ${activeStageIndex + 1} of ${STAGES.length}: ${copy.title}`}
          >
            {STAGES.map((stage, index) => {
              const isActive = index === activeStageIndex;
              const isDone = index < activeStageIndex;
              return (
                <React.Fragment key={stage.phase}>
                  {index > 0 && (
                    <span
                      aria-hidden="true"
                      className={`h-px w-4 sm:w-6 ${isDone || isActive ? "bg-[#FFBC00]/50" : "bg-white/15"}`}
                    />
                  )}
                  <span
                    className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 font-inter text-[11px] transition-colors ${
                      isActive
                        ? "bg-[#FFBC00]/15 text-[#FFBC00]"
                        : isDone
                          ? "text-white/45"
                          : "text-white/25"
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`h-1.5 w-1.5 rounded-full ${
                        isActive
                          ? "bg-[#FFBC00] voice-stage-pulse"
                          : isDone
                            ? "bg-white/40"
                            : "bg-white/15"
                      }`}
                    />
                    {stage.label}
                  </span>
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>

      <style>{`
        @keyframes voice-eq-bar-idle {
          0%, 100% { transform: scaleY(0.6); opacity: 0.35; }
          50% { transform: scaleY(1); opacity: 0.7; }
        }
        .voice-eq-bar-idle {
          animation: voice-eq-bar-idle 1.4s ease-in-out infinite;
          transform-origin: center;
        }
        @keyframes voice-stage-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.45; transform: scale(0.75); }
        }
        .voice-stage-pulse {
          animation: voice-stage-pulse 1.1s ease-in-out infinite;
        }
      `}</style>
    </VoicePanelShell>
  );
};

export default VoiceListeningPanel;
